// Milestone Alerts Component - Hospice CareLens
// Provides toast notifications and badge indicators for strategic milestones
// Based on 5-Year Strategic Plan: 170 → 450 ADC

import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import {
  Target, TrendingUp, AlertTriangle, CheckCircle2, Bell,
  Milestone, Award, Rocket, Flag, Trophy, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Strategic Plan Milestones from 5-Year Plan
const MILESTONES = {
  phase_1: {
    id: 'phase_1',
    name: 'Foundation & Stabilization',
    adc_target: 170,
    year: 1,
    icon: Flag,
    color: 'slate',
    description: 'Stabilize operations, establish baseline metrics',
    key_metrics: {
      weekly_admissions: 15,
      rncm_count: 12,
      compliance_rate: 92
    }
  },
  phase_2: {
    id: 'phase_2',
    name: 'Controlled Growth',
    adc_target: 250,
    year: 2,
    icon: TrendingUp,
    color: 'blue',
    description: 'Expand census through strategic partnerships',
    key_metrics: {
      weekly_admissions: 22,
      rncm_count: 18,
      compliance_rate: 94
    }
  },
  phase_3: {
    id: 'phase_3',
    name: 'Regional Expansion',
    adc_target: 350,
    year: 3,
    icon: Rocket,
    color: 'violet',
    description: 'Expand geographic footprint and services',
    key_metrics: {
      weekly_admissions: 30,
      rncm_count: 25,
      compliance_rate: 95
    }
  },
  phase_4: {
    id: 'phase_4',
    name: 'Market Leadership',
    adc_target: 400,
    year: 4,
    icon: Award,
    color: 'amber',
    description: 'Establish market leadership position',
    key_metrics: {
      weekly_admissions: 35,
      rncm_count: 30,
      compliance_rate: 96
    }
  },
  phase_5: {
    id: 'phase_5',
    name: 'Excellence & Sustainability',
    adc_target: 450,
    year: 5,
    icon: Trophy,
    color: 'emerald',
    description: 'Achieve target census and sustainable excellence',
    key_metrics: {
      weekly_admissions: 40,
      rncm_count: 35,
      compliance_rate: 97
    }
  }
};

// Milestone Alert Types
const ALERT_TYPES = {
  MILESTONE_ACHIEVED: 'milestone_achieved',
  MILESTONE_APPROACHING: 'milestone_approaching',
  MILESTONE_AT_RISK: 'milestone_at_risk',
  TRAJECTORY_POSITIVE: 'trajectory_positive',
  TRAJECTORY_NEGATIVE: 'trajectory_negative',
  GROWTH_ACCELERATION: 'growth_acceleration',
  TARGET_MET: 'target_met'
};

// Badge Component for Milestone Status
export function MilestoneBadge({ status, size = 'default', showIcon = true, className = '' }) {
  const badgeConfig = {
    achieved: {
      className: 'bg-gradient-to-r from-emerald-500 to-green-500 text-white border-0',
      icon: CheckCircle2,
      label: 'Achieved'
    },
    in_progress: {
      className: 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white border-0',
      icon: Target,
      label: 'In Progress'
    },
    approaching: {
      className: 'bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0',
      icon: Zap,
      label: 'Approaching'
    },
    at_risk: {
      className: 'bg-gradient-to-r from-red-500 to-rose-500 text-white border-0',
      icon: AlertTriangle,
      label: 'At Risk'
    },
    upcoming: {
      className: 'bg-gradient-to-r from-slate-400 to-slate-500 text-white border-0',
      icon: Milestone,
      label: 'Upcoming'
    }
  };

  const config = badgeConfig[status] || badgeConfig.upcoming;
  const Icon = config.icon;

  return (
    <Badge 
      className={`${config.className} ${size === 'lg' ? 'px-3 py-1.5 text-sm' : 'px-2 py-0.5 text-xs'} ${className}`}
      data-testid={`milestone-badge-${status}`}
    >
      {showIcon && <Icon className={`${size === 'lg' ? 'h-4 w-4' : 'h-3 w-3'} mr-1`} />}
      {config.label}
    </Badge>
  );
}

// Progress Badge for Phase Progress
export function PhaseProgressBadge({ currentAdc, phaseTarget, phaseName }) {
  const progress = Math.min(100, Math.round((currentAdc / phaseTarget) * 100));
  const isComplete = currentAdc >= phaseTarget;
  const isClose = progress >= 90;

  return (
    <div className="flex items-center gap-2" data-testid="phase-progress-badge">
      <div className={`
        px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5
        ${isComplete ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
          isClose ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'}
      `}>
        {isComplete ? (
          <>
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span>{phaseName} Complete</span>
          </>
        ) : (
          <>
            <Target className="h-3.5 w-3.5" />
            <span>{progress}% to {phaseName}</span>
          </>
        )}
      </div>
    </div>
  );
}

// Notification Count Badge
export function AlertCountBadge({ count, variant = 'default' }) {
  if (count === 0) return null;

  const variants = {
    default: 'bg-red-500 text-white',
    warning: 'bg-amber-500 text-white',
    success: 'bg-emerald-500 text-white',
    info: 'bg-blue-500 text-white'
  };

  return (
    <span 
      className={`
        absolute -top-1 -right-1 flex items-center justify-center
        min-w-[18px] h-[18px] text-[10px] font-bold rounded-full
        ${variants[variant]}
      `}
      data-testid="alert-count-badge"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

// Main Milestone Alerts Hook
export function useMilestoneAlerts() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastChecked, setLastChecked] = useState(null);

  const checkMilestones = useCallback(async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/strategic-plan/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const newAlerts = generateAlerts(data);
        setAlerts(newAlerts);
        setLastChecked(new Date());

        // Show toast notifications for new critical alerts
        newAlerts
          .filter(alert => alert.priority === 'high' && !alert.dismissed)
          .slice(0, 3)
          .forEach((alert, index) => {
            setTimeout(() => {
              showMilestoneToast(alert);
            }, index * 1000);
          });
      }
    } catch (error) {
      console.error('Error checking milestones:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  return { alerts, loading, lastChecked, checkMilestones };
}

// Generate alerts based on strategic data
function generateAlerts(data) {
  const alerts = [];
  const { current_performance, gap_analysis, trajectory, phases } = data;
  
  const currentAdc = current_performance?.adc || 50;
  const currentPhase = current_performance?.phase || 'phase_1';
  const milestone = MILESTONES[currentPhase];
  
  // Check if approaching a milestone (within 10% of target)
  const progressToPhase = (currentAdc / milestone.adc_target) * 100;
  
  if (progressToPhase >= 90 && progressToPhase < 100) {
    alerts.push({
      id: `approaching-${currentPhase}`,
      type: ALERT_TYPES.MILESTONE_APPROACHING,
      title: `Approaching ${milestone.name}`,
      message: `Only ${milestone.adc_target - currentAdc} ADC away from achieving ${milestone.name} milestone!`,
      priority: 'high',
      phase: currentPhase,
      icon: Zap,
      color: 'amber',
      action: 'View Progress',
      actionLink: '/strategic-plan'
    });
  }

  // Check if milestone achieved
  if (currentAdc >= milestone.adc_target) {
    const nextPhase = getNextPhase(currentPhase);
    if (nextPhase) {
      alerts.push({
        id: `achieved-${currentPhase}`,
        type: ALERT_TYPES.MILESTONE_ACHIEVED,
        title: `${milestone.name} Achieved!`,
        message: `Congratulations! You've reached ${milestone.adc_target} ADC. Next target: ${MILESTONES[nextPhase].adc_target} ADC`,
        priority: 'high',
        phase: currentPhase,
        icon: Trophy,
        color: 'emerald',
        action: 'Celebrate',
        actionLink: '/strategic-plan'
      });
    }
  }

  // Check trajectory
  if (trajectory?.on_track === false) {
    alerts.push({
      id: 'trajectory-at-risk',
      type: ALERT_TYPES.MILESTONE_AT_RISK,
      title: 'Growth Trajectory Alert',
      message: `Current growth rate (${trajectory.current_net_growth}/week) is below required (${trajectory.required_weekly_growth}/week)`,
      priority: 'high',
      icon: AlertTriangle,
      color: 'red',
      action: 'View Analysis',
      actionLink: '/strategic-plan'
    });
  } else if (trajectory?.assessment === 'Exceeding Plan') {
    alerts.push({
      id: 'trajectory-positive',
      type: ALERT_TYPES.GROWTH_ACCELERATION,
      title: 'Exceeding Growth Targets!',
      message: `Outstanding! Net growth of ${trajectory.current_net_growth}/week exceeds the required ${trajectory.required_weekly_growth}/week`,
      priority: 'medium',
      icon: Rocket,
      color: 'emerald',
      action: 'View Details',
      actionLink: '/strategic-plan'
    });
  }

  // Check gap analysis
  if (gap_analysis?.assessment === 'Critical Gap') {
    alerts.push({
      id: 'critical-gap',
      type: ALERT_TYPES.MILESTONE_AT_RISK,
      title: 'Critical Performance Gap',
      message: `Significant gap detected: ${gap_analysis.overall_gap_score}% behind phase targets`,
      priority: 'high',
      icon: AlertTriangle,
      color: 'red',
      action: 'View Gap Analysis',
      actionLink: '/strategic-plan'
    });
  }

  // Key metric alerts
  if (gap_analysis?.workforce?.gap_to_phase > 5) {
    alerts.push({
      id: 'workforce-gap',
      type: ALERT_TYPES.MILESTONE_AT_RISK,
      title: 'Staffing Gap Alert',
      message: `Need to hire ${gap_analysis.workforce.gap_to_phase} more RNCMs to support growth`,
      priority: 'medium',
      icon: AlertTriangle,
      color: 'amber',
      action: 'View Workforce',
      actionLink: '/workforce-intelligence'
    });
  }

  return alerts;
}

// Get next phase in sequence
function getNextPhase(currentPhase) {
  const phaseOrder = ['phase_1', 'phase_2', 'phase_3', 'phase_4', 'phase_5'];
  const currentIndex = phaseOrder.indexOf(currentPhase);
  if (currentIndex < phaseOrder.length - 1) {
    return phaseOrder[currentIndex + 1];
  }
  return null;
}

// Show toast notification for milestone
export function showMilestoneToast(alert) {
  const Icon = alert.icon;
  
  const toastConfig = {
    duration: 8000,
    icon: <Icon className={`h-5 w-5 text-${alert.color}-500`} />,
  };

  switch (alert.type) {
    case ALERT_TYPES.MILESTONE_ACHIEVED:
      toast.success(alert.title, {
        ...toastConfig,
        description: alert.message,
        action: alert.action ? {
          label: alert.action,
          onClick: () => window.location.href = alert.actionLink
        } : undefined
      });
      break;
      
    case ALERT_TYPES.MILESTONE_APPROACHING:
      toast(alert.title, {
        ...toastConfig,
        description: alert.message,
        style: { borderLeft: '4px solid #f59e0b' }
      });
      break;
      
    case ALERT_TYPES.MILESTONE_AT_RISK:
      toast.warning(alert.title, {
        ...toastConfig,
        description: alert.message,
        action: alert.action ? {
          label: alert.action,
          onClick: () => window.location.href = alert.actionLink
        } : undefined
      });
      break;
      
    case ALERT_TYPES.GROWTH_ACCELERATION:
      toast.success(alert.title, {
        ...toastConfig,
        description: alert.message,
      });
      break;
      
    default:
      toast(alert.title, {
        ...toastConfig,
        description: alert.message,
      });
  }
}

// Milestone Alert Card Component
export function MilestoneAlertCard({ alert, onDismiss }) {
  const Icon = alert.icon || Bell;
  
  const colorClasses = {
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    red: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    blue: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
  };

  const iconColorClasses = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
    blue: 'text-blue-600 dark:text-blue-400'
  };

  return (
    <Card 
      className={`${colorClasses[alert.color] || colorClasses.blue} border transition-all hover:shadow-md`}
      data-testid={`milestone-alert-${alert.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-white/50 dark:bg-black/20`}>
            <Icon className={`h-5 w-5 ${iconColorClasses[alert.color] || iconColorClasses.blue}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-sm">{alert.title}</h4>
            <p className="text-sm text-muted-foreground mt-0.5">{alert.message}</p>
            {alert.action && (
              <Button 
                variant="link" 
                size="sm" 
                className="p-0 h-auto mt-2 text-xs"
                onClick={() => window.location.href = alert.actionLink}
              >
                {alert.action} →
              </Button>
            )}
          </div>
          {onDismiss && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
              onClick={() => onDismiss(alert.id)}
            >
              ×
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// Export milestone data for use in other components
export { MILESTONES, ALERT_TYPES };

export default {
  MilestoneBadge,
  PhaseProgressBadge,
  AlertCountBadge,
  MilestoneAlertCard,
  useMilestoneAlerts,
  showMilestoneToast,
  MILESTONES,
  ALERT_TYPES
};
